import type { FlightDto, StatsDto } from '@flightlog/shared';
import { formatDistance, localParts, plural } from '../format';
import { PlaneIcon } from '../icons';
import { haptic } from '../telegram';
import { Board } from './Board';

interface Props {
  flights: FlightDto[];
  stats: StatsDto | null;
  onSelect: (flight: FlightDto) => void;
  onAdd: () => void;
}

export function FlightCard({ flight, onSelect }: { flight: FlightDto; onSelect: (f: FlightDto) => void }) {
  const dep = localParts(flight.scheduledDepUtc, flight.dep.tz);
  const arr = localParts(flight.scheduledArrUtc, flight.arr.tz);
  const nextDay = arr.date !== dep.date;
  const upcoming = new Date(flight.scheduledDepUtc).getTime() > Date.now();

  return (
    <button
      type="button"
      className="flight"
      onClick={() => {
        haptic();
        onSelect(flight);
      }}
    >
      <div className="flight-date">
        <b>{dep.day}</b>
        <span>{dep.month}</span>
      </div>

      <div className="flight-body">
        <div className="flight-route">
          <span className="code">{flight.dep.iata}</span>
          <span className="route-line" aria-hidden="true">
            <i />
            <PlaneIcon size={13} />
            <i />
          </span>
          <span className="code">{flight.arr.iata}</span>
        </div>

        <div className="flight-times">
          <span>{dep.time}</span>
          <span>
            {flight.timesEstimated ? '≈' : ''}
            {arr.time}
            {nextDay ? <sup>+1</sup> : null}
          </span>
        </div>

        <div className="flight-meta">
          <span>
            {flight.airline?.name ?? 'Рейс'} · {flight.flightNumber}
          </span>
          {upcoming && flight.status !== 'cancelled' ? (
            <em className="chip chip-soft">предстоит</em>
          ) : null}
          {flight.status === 'cancelled' ? <em className="chip chip-danger">отменён</em> : null}
          {flight.status === 'delayed' ? <em className="chip chip-warn">задержка</em> : null}
        </div>
      </div>
    </button>
  );
}

export function FlightsView({ flights, stats, onSelect, onAdd }: Props) {
  const now = Date.now();
  const upcoming = flights
    .filter((flight) => new Date(flight.scheduledDepUtc).getTime() > now)
    .sort((a, b) => a.scheduledDepUtc.localeCompare(b.scheduledDepUtc));

  const groups = new Map<string, FlightDto[]>();
  for (const flight of flights) {
    const year = flight.depLocalDate.slice(0, 4);
    const bucket = groups.get(year) ?? [];
    bucket.push(flight);
    groups.set(year, bucket);
  }

  const years = [...groups.entries()].sort((a, b) => b[0].localeCompare(a[0]));

  if (flights.length === 0) {
    return (
      <div className="empty">
        <div className="empty-icon">
          <PlaneIcon size={34} />
        </div>
        <h2>Пока пусто</h2>
        <p>Добавь первый рейс или импортируй историю из App in the Air.</p>
        <button type="button" className="button button-primary" onClick={onAdd}>
          Добавить рейс
        </button>
      </div>
    );
  }

  return (
    <div className="stack">
      {stats && stats.totalFlights > 0 ? <Board stats={stats} /> : null}

      {upcoming.length > 0 ? (
        <section className="section">
          <h2 className="section-title">Ближайший рейс</h2>
          <FlightCard flight={upcoming[0]!} onSelect={onSelect} />
        </section>
      ) : null}

      {years.map(([year, items]) => {
        const km = items.reduce((sum, flight) => sum + (flight.distanceKm ?? 0), 0);
        return (
          <section className="section" key={year}>
            <div className="section-head">
              <h2 className="section-title">{year}</h2>
              <span className="section-note">
                {items.length} {plural(items.length, ['рейс', 'рейса', 'рейсов'])} ·{' '}
                {formatDistance(km)}
              </span>
            </div>
            <div className="list">
              {items.map((flight) => (
                <FlightCard key={flight.id} flight={flight} onSelect={onSelect} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
